/**
 * SheetSetPanel — the Sheet Set Manager palette (Faz 10.13, OCS
 * sheet_set.rs parity). Manages the document's sheet set: subsets, sheets
 * (bound to paper-space layouts), the per-sheet "Include for Publish" flag
 * and the sheet number/title/description fields. Sheets plot through
 * PUBLISH's "from Sheet Set" source into one multi-page PDF; the set
 * round-trips inside the .yap envelope (cadScene.sheetSet).
 *
 * Right-click menus mirror the OCS fields (New Sheet / New Subset / Remove /
 * Rename / Publish); ordering uses the up/down items so nothing ever clips.
 */

import { useMemo, useState, type ReactNode } from 'react';
import {
    Box, Button, Checkbox, Flex, HStack, IconButton, Input, Modal,
    ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
    ModalOverlay, Text, Tooltip, VStack, Select,
} from '@chakra-ui/react';
import {
    ChevronDown, ChevronRight, Pencil, Plus, Printer, Trash2,
    ArrowDown, ArrowUp, FileQuestion,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
    useSheetSetStore,
    type SheetSetSheet,
    type SheetSetSubset,
} from '../../state/sheetSetStore';
import { useLayoutStore } from '../../state/useLayoutStore';
import { openPlotDialog } from '../../services/plot/plotDialogBridge';

type PanelModal =
    | { kind: 'newSubset' }
    | { kind: 'newSheet'; subsetId: string }
    | { kind: 'renameSet' }
    | { kind: 'renameSubset'; subsetId: string }
    | { kind: 'renameSheet'; subsetId: string; sheetId: string }
    | { kind: 'deleteSet' }
    | { kind: 'deleteSubset'; subsetId: string }
    | { kind: 'deleteSheet'; subsetId: string; sheetId: string }
    | null;

interface PanelMenu {
    x: number;
    y: number;
    kind: 'subset' | 'sheet' | 'blank';
    subsetId?: string;
    sheetId?: string;
}

const I18N = 'ui.cad.sheetSet';

export function SheetSetPanel() {
    const { t } = useTranslation();
    const set = useSheetSetStore((s) => s.set);
    const store = useSheetSetStore();
    const layouts = useLayoutStore((s) => s.layouts);

    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [menu, setMenu] = useState<PanelMenu | null>(null);
    const [modal, setModal] = useState<PanelModal>(null);
    const [nameDraft, setNameDraft] = useState('');
    const [sheetDraft, setSheetDraft] = useState<{ layoutId: string; number: string; title: string; description: string }>(
        { layoutId: '', number: '', title: '', description: '' },
    );

    const closeMenu = () => setMenu(null);
    const toggleExpanded = (subsetId: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(subsetId)) next.delete(subsetId); else next.add(subsetId);
            return next;
        });
    };

    const includeCount = useMemo(() => {
        if (!set) return { on: 0, total: 0 };
        let on = 0;
        let total = 0;
        for (const sub of set.subsets) {
            for (const sheet of sub.sheets) {
                total++;
                if (sheet.includeForPublish) on++;
            }
        }
        return { on, total };
    }, [set]);

    const openRename = (target: NonNullable<PanelModal>) => {
        if (target.kind === 'renameSet') setNameDraft(set?.name ?? '');
        else if (target.kind === 'renameSubset') {
            const sub = set?.subsets.find((s) => s.id === target.subsetId);
            setNameDraft(sub?.name ?? '');
        } else if (target.kind === 'renameSheet') {
            const sheet = set?.subsets.find((s) => s.id === target.subsetId)?.sheets.find((sh) => sh.id === target.sheetId);
            setNameDraft(sheet?.title ?? '');
        }
        setModal(target);
    };

    const openNewSheet = (subsetId: string) => {
        const first = layouts[0];
        setSheetDraft({
            layoutId: first?.id ?? '',
            number: '',
            title: first?.name ?? '',
            description: '',
        });
        setModal({ kind: 'newSheet', subsetId });
    };

    const confirmRename = () => {
        if (!modal) return;
        const name = nameDraft.trim();
        if (modal.kind === 'renameSet' && name) store.renameSet(name);
        else if (modal.kind === 'renameSubset' && name) store.renameSubset(modal.subsetId, name);
        else if (modal.kind === 'renameSheet' && name) store.updateSheet(modal.subsetId, modal.sheetId, { title: name });
        setModal(null);
    };

    const confirmNewSheet = () => {
        if (modal?.kind !== 'newSheet') return;
        const layout = layouts.find((l) => l.id === sheetDraft.layoutId);
        const sheet: Omit<SheetSetSheet, 'id'> = {
            title: sheetDraft.title.trim() || layout?.name || 'Sheet',
            layoutId: layout?.id ?? null,
            number: sheetDraft.number.trim(),
            description: sheetDraft.description.trim(),
            includeForPublish: true,
        };
        store.addSheet(modal.subsetId, sheet);
        setModal(null);
    };

    const openDeleteTarget = (target: NonNullable<PanelModal>) => setModal(target);

    const confirmDelete = () => {
        if (!modal) return;
        if (modal.kind === 'deleteSet') store.deleteSet();
        else if (modal.kind === 'deleteSubset') store.removeSubset(modal.subsetId);
        else if (modal.kind === 'deleteSheet') store.removeSheet(modal.subsetId, modal.sheetId);
        setModal(null);
    };

    const menuItems = (m: PanelMenu): { key: string; label: string; danger?: boolean; icon?: ReactNode; run: () => void }[] => {
        if (m.kind === 'subset' && m.subsetId) {
            const id = m.subsetId;
            return [
                { key: 'newSheet', label: t(`${I18N}.newSheet`), icon: <Plus size={12} />, run: () => openNewSheet(id) },
                { key: 'rename', label: t(`${I18N}.rename`), icon: <Pencil size={12} />, run: () => openRename({ kind: 'renameSubset', subsetId: id }) },
                { key: 'moveUp', label: t(`${I18N}.moveUp`), run: () => store.moveSubset(id, -1) },
                { key: 'moveDown', label: t(`${I18N}.moveDown`), run: () => store.moveSubset(id, 1) },
                { key: 'remove', label: t(`${I18N}.remove`), danger: true, icon: <Trash2 size={12} />, run: () => openDeleteTarget({ kind: 'deleteSubset', subsetId: id }) },
            ];
        }
        if (m.kind === 'sheet' && m.subsetId && m.sheetId) {
            const sid = m.subsetId;
            const shid = m.sheetId;
            return [
                { key: 'publish', label: t(`${I18N}.publish`), icon: <Printer size={12} />, run: () => openPlotDialog({ mode: 'publish', source: 'sheetSet' }) },
                { key: 'rename', label: t(`${I18N}.rename`), icon: <Pencil size={12} />, run: () => openRename({ kind: 'renameSheet', subsetId: sid, sheetId: shid }) },
                { key: 'moveUp', label: t(`${I18N}.moveUp`), run: () => store.moveSheet(sid, shid, -1) },
                { key: 'moveDown', label: t(`${I18N}.moveDown`), run: () => store.moveSheet(sid, shid, 1) },
                { key: 'remove', label: t(`${I18N}.remove`), danger: true, icon: <Trash2 size={12} />, run: () => openDeleteTarget({ kind: 'deleteSheet', subsetId: sid, sheetId: shid }) },
            ];
        }
        return [
            { key: 'newSubset', label: t(`${I18N}.newSubset`), icon: <Plus size={12} />, run: () => { setNameDraft(''); setModal({ kind: 'newSubset' }); } },
            { key: 'renameSet', label: t(`${I18N}.renameSet`), icon: <Pencil size={12} />, run: () => openRename({ kind: 'renameSet' }) },
            { key: 'publish', label: t(`${I18N}.publish`), icon: <Printer size={12} />, run: () => openPlotDialog({ mode: 'publish', source: 'sheetSet' }) },
            { key: 'deleteSet', label: t(`${I18N}.deleteSet`), danger: true, icon: <Trash2 size={12} />, run: () => openDeleteTarget({ kind: 'deleteSet' }) },
        ];
    };

    // Empty state: no sheet set in this document yet.
    if (!set) {
        return (
            <Box h="full" p={3} overflowY="auto">
                <VStack spacing={3} pt={8} align="stretch">
                    <Text fontSize="sm" fontWeight="semibold" color="text.primary" textAlign="center">
                        {t(`${I18N}.emptyTitle`)}
                    </Text>
                    <Text fontSize="xs" color="text.secondary" textAlign="center">
                        {t(`${I18N}.emptyHint`)}
                    </Text>
                    <HStack justify="center">
                        <Input
                            size="sm"
                            placeholder={t(`${I18N}.setNamePlaceholder`)}
                            value={nameDraft}
                            onChange={(e) => setNameDraft(e.target.value)}
                            maxW="220px"
                        />
                        <Button
                            size="sm"
                            colorScheme="blue"
                            isDisabled={!nameDraft.trim()}
                            onClick={() => { store.createSet(nameDraft); setNameDraft(''); }}
                        >
                            {t(`${I18N}.create`)}
                        </Button>
                    </HStack>
                </VStack>
            </Box>
        );
    }

    return (
        <Box
            h="full"
            display="flex"
            flexDirection="column"
            minH="0"
            onContextMenu={(e) => {
                // Blank-area menu only when the target is the container itself.
                if (e.target === e.currentTarget) {
                    e.preventDefault();
                    setMenu({ x: e.clientX, y: e.clientY, kind: 'blank' });
                }
            }}
        >
            {/* Header: set name + set-level actions */}
            <Flex
                px={2}
                py={2}
                align="center"
                gap={1}
                borderBottom="1px solid"
                borderColor="border.base"
                flexShrink={0}
                onContextMenu={(e) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY, kind: 'blank' }); }}
            >
                <Tooltip label={t(`${I18N}.renameSet`)}>
                    <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="text.primary"
                        isTruncated
                        flex="1"
                        minW="0"
                        cursor="pointer"
                        onClick={() => openRename({ kind: 'renameSet' })}
                    >
                        {set.name}
                    </Text>
                </Tooltip>
                <Tooltip label={t(`${I18N}.newSubset`)}>
                    <IconButton
                        aria-label={t(`${I18N}.newSubset`)}
                        size="xs"
                        variant="ghost"
                        icon={<Plus size={13} />}
                        onClick={() => { setNameDraft(''); setModal({ kind: 'newSubset' }); }}
                    />
                </Tooltip>
                <Tooltip label={t(`${I18N}.publish`)}>
                    <IconButton
                        aria-label={t(`${I18N}.publish`)}
                        size="xs"
                        variant="ghost"
                        icon={<Printer size={13} />}
                        onClick={() => openPlotDialog({ mode: 'publish', source: 'sheetSet' })}
                    />
                </Tooltip>
                <Tooltip label={t(`${I18N}.deleteSet`)}>
                    <IconButton
                        aria-label={t(`${I18N}.deleteSet`)}
                        size="xs"
                        variant="ghost"
                        icon={<Trash2 size={13} />}
                        onClick={() => openDeleteTarget({ kind: 'deleteSet' })}
                    />
                </Tooltip>
            </Flex>

            {/* Tree: subsets → sheets */}
            <Box flex="1" minH="0" overflowY="auto" px={1} py={1}>
                {set.subsets.length === 0 && (
                    <Text fontSize="xs" color="text.secondary" px={2} py={3}>
                        {t(`${I18N}.noSubsetsHint`)}
                    </Text>
                )}
                {set.subsets.map((sub: SheetSetSubset) => {
                    const isOpen = expanded.has(sub.id);
                    return (
                        <Box key={sub.id}>
                            <Flex
                                align="center"
                                gap={1}
                                px={1}
                                py={1}
                                borderRadius="4px"
                                cursor="pointer"
                                _hover={{ bg: 'bg.hover' }}
                                whiteSpace="nowrap"
                                onContextMenu={(e) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY, kind: 'subset', subsetId: sub.id }); }}
                                onClick={() => toggleExpanded(sub.id)}
                            >
                                {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                                <Text fontSize="xs" color="text.primary" isTruncated minW="0" flex="1">
                                    {sub.name}
                                </Text>
                                <Text fontSize="2xs" color="text.secondary" flexShrink={0}>
                                    {sub.sheets.length}
                                </Text>
                            </Flex>
                            {isOpen && sub.sheets.map((sheet: SheetSetSheet) => (
                                <Flex
                                    key={sheet.id}
                                    align="center"
                                    gap={2}
                                    px={1}
                                    py={0.5}
                                    pl={6}
                                    borderRadius="4px"
                                    _hover={{ bg: 'bg.hover' }}
                                    whiteSpace="nowrap"
                                    onContextMenu={(e) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY, kind: 'sheet', subsetId: sub.id, sheetId: sheet.id }); }}
                                >
                                    <Checkbox
                                        size="sm"
                                        isChecked={sheet.includeForPublish}
                                        onChange={(e) => store.updateSheet(sub.id, sheet.id, { includeForPublish: e.target.checked })}
                                        flexShrink={0}
                                    />
                                    {sheet.number && (
                                        <Text fontSize="2xs" color="text.secondary" flexShrink={0}>
                                            {sheet.number}
                                        </Text>
                                    )}
                                    <Text fontSize="xs" color="text.primary" isTruncated minW="0" flex="1">
                                        {sheet.title}
                                    </Text>
                                    {sheet.layoutId && !layouts.some((l) => l.id === sheet.layoutId) && (
                                        <Tooltip label={t(`${I18N}.staleLayout`)}>
                                            <Box flexShrink={0} color="status.warning"><FileQuestion size={12} /></Box>
                                        </Tooltip>
                                    )}
                                    {!sheet.layoutId && (
                                        <Tooltip label={t(`${I18N}.unlinked`)}>
                                            <Box flexShrink={0} color="text.secondary"><FileQuestion size={12} /></Box>
                                        </Tooltip>
                                    )}
                                </Flex>
                            ))}
                        </Box>
                    );
                })}
            </Box>

            {/* Footer summary */}
            <Flex
                px={2}
                py={1.5}
                borderTop="1px solid"
                borderColor="border.base"
                flexShrink={0}
                align="center"
            >
                <Text fontSize="2xs" color="text.secondary" noOfLines={1}>
                    {t(`${I18N}.includeSummary`, { on: includeCount.on, total: includeCount.total })}
                </Text>
            </Flex>

            {/* Right-click menu (panel-local; closes on backdrop click) */}
            {menu && (
                <>
                    <Box position="fixed" inset={0} zIndex={40} onClick={closeMenu} onContextMenu={(e) => { e.preventDefault(); closeMenu(); }} />
                    <Box
                        position="fixed"
                        left={`${menu.x}px`}
                        top={`${menu.y}px`}
                        zIndex={41}
                        bg="bg.panel"
                        border="1px solid"
                        borderColor="border.base"
                        borderRadius="6px"
                        boxShadow="lg"
                        py={1}
                        minW="180px"
                        w="max-content"
                    >
                        {menuItems(menu).map((item) => (
                            <Flex
                                key={item.key}
                                as="button"
                                px={3}
                                py={1.5}
                                w="full"
                                align="center"
                                gap={2}
                                fontSize="xs"
                                color={item.danger ? 'red.400' : 'text.primary'}
                                whiteSpace="nowrap"
                                _hover={{ bg: 'bg.hover' }}
                                onClick={() => { closeMenu(); item.run(); }}
                            >
                                {item.icon}
                                {item.label}
                            </Flex>
                        ))}
                    </Box>
                </>
            )}

            {/* New subset / rename modals share one name prompt */}
            <Modal isOpen={modal?.kind === 'newSubset' || modal?.kind === 'renameSet' || modal?.kind === 'renameSubset' || modal?.kind === 'renameSheet'} onClose={() => setModal(null)} size="xs">
                <ModalOverlay />
                <ModalContent bg="bg.panel">
                    <ModalHeader fontSize="sm">
                        {modal?.kind === 'newSubset' && t(`${I18N}.newSubsetTitle`)}
                        {modal?.kind === 'renameSet' && t(`${I18N}.renameSetTitle`)}
                        {modal?.kind === 'renameSubset' && t(`${I18N}.renameSubsetTitle`)}
                        {modal?.kind === 'renameSheet' && t(`${I18N}.renameSheetTitle`)}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Input
                            size="sm"
                            variant="filled"
                            bg="surface.300"
                            color="text.primary"
                            value={nameDraft}
                            onChange={(e) => setNameDraft(e.target.value)}
                            autoFocus
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button size="sm" variant="ghost" mr={2} onClick={() => setModal(null)}>
                            {t('common.cancel')}
                        </Button>
                        <Button size="sm" colorScheme="blue" isDisabled={!nameDraft.trim()} onClick={confirmRename}>
                            {t('common.ok')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* New sheet: bind a layout + sheet fields */}
            <Modal isOpen={modal?.kind === 'newSheet'} onClose={() => setModal(null)} size="xs">
                <ModalOverlay />
                <ModalContent bg="bg.panel">
                    <ModalHeader fontSize="sm">{t(`${I18N}.newSheetTitle`)}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={2} align="stretch">
                            <Text fontSize="2xs" color="text.secondary">{t(`${I18N}.sheetLayoutLabel`)}</Text>
                            <Select
                                size="sm"
                                value={sheetDraft.layoutId}
                                isDisabled={layouts.length === 0}
                                onChange={(e) => {
                                    const layout = layouts.find((l) => l.id === e.target.value);
                                    setSheetDraft((d) => ({
                                        ...d,
                                        layoutId: e.target.value,
                                        title: layout?.name ?? d.title,
                                    }));
                                }}
                            >
                                {layouts.map((l) => (
                                    <option key={l.id} value={l.id}>{l.name}</option>
                                ))}
                            </Select>
                            {layouts.length === 0 && (
                                <Text fontSize="2xs" color="status.warning">{t(`${I18N}.noLayoutsWarning`)}</Text>
                            )}
                            <Text fontSize="2xs" color="text.secondary">{t(`${I18N}.sheetNumberLabel`)}</Text>
                            <Input
                                size="sm"
                                variant="filled"
                                bg="surface.300"
                                color="text.primary"
                                value={sheetDraft.number}
                                onChange={(e) => setSheetDraft((d) => ({ ...d, number: e.target.value }))}
                            />
                            <Text fontSize="2xs" color="text.secondary">{t(`${I18N}.sheetTitleLabel`)}</Text>
                            <Input
                                size="sm"
                                variant="filled"
                                bg="surface.300"
                                color="text.primary"
                                value={sheetDraft.title}
                                onChange={(e) => setSheetDraft((d) => ({ ...d, title: e.target.value }))}
                            />
                            <Text fontSize="2xs" color="text.secondary">{t(`${I18N}.sheetDescriptionLabel`)}</Text>
                            <Input
                                size="sm"
                                variant="filled"
                                bg="surface.300"
                                color="text.primary"
                                value={sheetDraft.description}
                                onChange={(e) => setSheetDraft((d) => ({ ...d, description: e.target.value }))}
                            />
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button size="sm" variant="ghost" mr={2} onClick={() => setModal(null)}>
                            {t('common.cancel')}
                        </Button>
                        <Button size="sm" colorScheme="blue" onClick={confirmNewSheet}>
                            {t('common.ok')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Delete confirmations */}
            <Modal isOpen={modal?.kind === 'deleteSet' || modal?.kind === 'deleteSubset' || modal?.kind === 'deleteSheet'} onClose={() => setModal(null)} size="xs">
                <ModalOverlay />
                <ModalContent bg="bg.panel">
                    <ModalHeader fontSize="sm">{t(`${I18N}.deleteConfirmTitle`)}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="xs" color="text.secondary">
                            {modal?.kind === 'deleteSet' && t(`${I18N}.deleteSetBody`)}
                            {modal?.kind === 'deleteSubset' && t(`${I18N}.deleteSubsetBody`)}
                            {modal?.kind === 'deleteSheet' && t(`${I18N}.deleteSheetBody`)}
                        </Text>
                    </ModalBody>
                    <ModalFooter>
                        <Button size="sm" variant="ghost" mr={2} onClick={() => setModal(null)}>
                            {t('common.cancel')}
                        </Button>
                        <Button size="sm" colorScheme="red" onClick={confirmDelete}>
                            {t(`${I18N}.remove`)}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
